/**
 * Formata um telefone brasileiro progressivamente, conforme o usuário digita.
 * Fixo (10 dígitos): (11) 3214-5678 · Celular (11 dígitos): (11) 91234-5678
 *
 * @param {string} value
 * @returns {string}
 */
export function formatPhoneBR(value) {
  const digits = String(value).replace(/\D/g, '').slice(0, 11);
  if (digits.length === 0) return '';

  let formatted = `(${digits.slice(0, 2)}`;
  if (digits.length < 3) return formatted;

  // O bloco do meio vira 5 dígitos só quando o 11º dígito chega (celular).
  const middleLength = digits.length > 10 ? 5 : 4;
  const middleEnd = 2 + middleLength;

  formatted += `) ${digits.slice(2, middleEnd)}`;
  if (digits.length <= middleEnd) return formatted;

  formatted += `-${digits.slice(middleEnd, 11)}`;
  return formatted;
}

/**
 * @param {string} value
 * @returns {boolean}
 */
export function isValidEmail(value) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(String(value).trim());
}

/**
 * Fixo: 10 dígitos. Celular: 11 dígitos, com o nono dígito obrigatoriamente 9.
 *
 * @param {string} value
 * @returns {boolean}
 */
export function isValidPhoneBR(value) {
  const digits = String(value).replace(/\D/g, '');
  if (digits.length === 10) return true;
  if (digits.length === 11) return digits[2] === '9';
  return false;
}

/*
 * Tudo abaixo depende do DOM. O guard permite importar as funções puras acima
 * no Node (`node --test`), onde `HTMLElement` não existe.
 */
if (typeof window !== 'undefined' && typeof HTMLElement !== 'undefined') {
  const FIELDS = [
    { key: 'name', selector: '[name="contact[Nome completo]"]' },
    { key: 'email', selector: '[name="contact[email]"]' },
    { key: 'phone', selector: '[name="contact[WhatsApp]"]' },
  ];

  /** Quanto o toast fica na tela antes de começar a sair. */
  const TOAST_VISIBLE_MS = 3200;

  /** Duração da saída. Deve casar com a animação `capture-form__toast-out` do CSS. */
  const TOAST_EXIT_MS = 180;

  class CaptureForm extends HTMLElement {
    #form = null;
    #button = null;
    #inputs = {};
    #blurHandlers = new Map();
    #toast = null;
    #toastExitTimer = null;
    #toastHideTimer = null;

    connectedCallback() {
      this.#form = this.querySelector('form');
      if (!this.#form) return;

      this.#button = this.querySelector('.capture-form__submit');

      for (const { key, selector } of FIELDS) {
        this.#inputs[key] = this.#form.querySelector(selector);
      }

      if (this.#inputs.phone) {
        this.#inputs.phone.addEventListener('input', this.#handlePhoneInput);
      }

      for (const { key } of FIELDS) {
        const handler = () => this.#validateField(key);
        this.#blurHandlers.set(key, handler);
        this.#inputs[key]?.addEventListener('blur', handler);
      }

      /*
       * O toast é escrito dentro do form (o fallback nativo precisa do objeto `form` do
       * Liquid), mas não pode VIVER lá. Dois ancestrais o sabotam:
       *
       *  - .capture__panel tem z-index, logo cria um contexto de empilhamento. O z-index
       *    do toast passa a competir dentro dele, e o logo da seção fica por cima.
       *  - <animate-component> aplica `filter` e `will-change` durante a animação de
       *    entrada. Qualquer um dos dois vira bloco contentor, e um `position: fixed`
       *    ancora nele em vez da viewport — o toast aparece na altura do form e só sobe
       *    quando o GSAP limpa o filtro.
       *
       * Realocar para <body> elimina as duas causas de uma vez.
       */
      this.#toast = this.querySelector('.capture-form__success');
      if (this.#toast) document.body.append(this.#toast);

      /*
       * No fallback nativo o Shopify re-renderiza a página com o toast já visível. Ele
       * nasce no HTML, nunca passa por #showSuccess, e mesmo assim precisa sair sozinho.
       * Isto fica ANTES do return abaixo de propósito: vale com ou sem webhook.
       */
      if (this.#toast && !this.#toast.hidden) this.#scheduleToastDismissal(this.#toast);

      // Sem webhook, o {% form 'contact' %} nativo do Shopify submete sozinho.
      if (!this.dataset.webhookUrl) return;

      /*
       * A validação nativa do navegador (required / type="email") roda ANTES do
       * evento submit e bloqueia o envio com o balão de erro nativo (no idioma do
       * navegador, muitas vezes inglês numa página em português) — #handleSubmit
       * nunca executa e as mensagens de erro estilizadas em .capture-form__error
       * nunca aparecem. `novalidate` é setado aqui, e só aqui: dentro do caminho
       * com webhook configurado E o JS realmente rodando. Se o JS falhar ao
       * carregar, ou não houver webhook, os atributos `required` nativos do
       * Liquid continuam protegendo o POST nativo do Shopify. Não mova isso para
       * o snippets/capture-form.liquid.
       */
      this.#form.setAttribute('novalidate', '');

      this.#form.addEventListener('submit', this.#handleSubmit);
    }

    disconnectedCallback() {
      this.#inputs.phone?.removeEventListener('input', this.#handlePhoneInput);
      this.#form?.removeEventListener('submit', this.#handleSubmit);
      // Não remove o atributo `novalidate` aqui: o elemento está sendo descartado,
      // então reverter o estado do form não tem nenhum efeito observável.

      for (const [key, handler] of this.#blurHandlers) {
        this.#inputs[key]?.removeEventListener('blur', handler);
      }
      this.#blurHandlers.clear();

      // O editor de tema descarta e recria a seção a cada mudança de setting. Um timer
      // sobrevivente dispararia contra um nó órfão.
      this.#clearToastTimers();

      // O toast vive em <body>, fora desta subárvore — ninguém o recolhe por nós. Sem
      // isto, cada re-render do editor deixaria mais um toast pendurado no documento.
      this.#toast?.remove();
      this.#toast = null;
    }

    #handlePhoneInput = (event) => {
      event.target.value = formatPhoneBR(event.target.value);
    };

    /**
     * @param {string} key
     * @returns {string} mensagem de erro, ou '' se o campo estiver válido
     */
    #errorFor(key) {
      const value = this.#inputs[key]?.value.trim() ?? '';
      const ds = this.dataset;

      if (key === 'name') {
        return value ? '' : ds.errorName ?? '';
      }
      if (key === 'email') {
        if (!value) return ds.errorEmailRequired ?? '';
        return isValidEmail(value) ? '' : ds.errorEmailInvalid ?? '';
      }
      if (key === 'phone') {
        if (!value) return ds.errorPhoneRequired ?? '';
        return isValidPhoneBR(value) ? '' : ds.errorPhoneInvalid ?? '';
      }
      return '';
    }

    /**
     * @param {string} key
     * @returns {boolean} true se válido
     */
    #validateField(key) {
      const message = this.#errorFor(key);
      const input = this.#inputs[key];
      const slot = this.querySelector(`.capture-form__error[data-error-for="${key}"]`);

      if (!input) return true;

      input.setAttribute('aria-invalid', message ? 'true' : 'false');
      if (slot) {
        slot.textContent = message;
        slot.hidden = !message;
      }
      return !message;
    }

    #validateAll() {
      // `.every` faria short-circuit e deixaria campos sem marcar. `.filter` valida todos.
      return FIELDS.filter(({ key }) => !this.#validateField(key)).length === 0;
    }

    #setSubmitError(message) {
      const slot = this.querySelector('.capture-form__submit-error');
      if (!slot) return;
      slot.textContent = message;
      slot.hidden = !message;
    }

    #showSuccess() {
      // NÃO usar querySelector aqui: o nó foi movido para <body> no connectedCallback.
      const slot = this.#toast;
      if (!slot) return;
      slot.textContent = this.dataset.successMessage ?? '';

      // Um segundo envio reinicia a contagem em vez de herdar o timer do anterior.
      this.#clearToastTimers();
      slot.classList.remove('capture-form__success--leaving');
      slot.hidden = false;
      this.#scheduleToastDismissal(slot);
    }

    /**
     * Deixa o toast na tela por TOAST_VISIBLE_MS, anima a saída, e só então o esconde.
     * Esconder antes do fim da animação a cortaria pela metade.
     *
     * @param {HTMLElement} slot
     */
    #scheduleToastDismissal(slot) {
      this.#toastExitTimer = setTimeout(() => {
        slot.classList.add('capture-form__success--leaving');

        this.#toastHideTimer = setTimeout(() => {
          slot.hidden = true;
          slot.classList.remove('capture-form__success--leaving');
        }, TOAST_EXIT_MS);
      }, TOAST_VISIBLE_MS);
    }

    #clearToastTimers() {
      clearTimeout(this.#toastExitTimer);
      clearTimeout(this.#toastHideTimer);
      this.#toastExitTimer = null;
      this.#toastHideTimer = null;
    }

    #handleSubmit = async (event) => {
      event.preventDefault();
      this.#setSubmitError('');

      // Honeypot: bot preencheu um campo invisível. Finge sucesso, não envia nada.
      const honeypot = this.querySelector('.capture-form__hp');
      if (honeypot instanceof HTMLInputElement && honeypot.value !== '') {
        this.#showSuccess();
        this.#form.reset();
        return;
      }

      if (!this.#validateAll()) {
        const firstInvalid = FIELDS.map(({ key }) => this.#inputs[key]).find(
          (input) => input?.getAttribute('aria-invalid') === 'true'
        );
        firstInvalid?.focus();
        return;
      }

      const payload = new URLSearchParams({
        name: this.#inputs.name.value.trim(),
        email: this.#inputs.email.value.trim(),
        phone: this.#inputs.phone.value.trim(),
        source: window.location.href,
        timestamp: new Date().toISOString(),
      });

      if (this.#button) {
        this.#button.disabled = true;
        this.#button.setAttribute('aria-busy', 'true');
      }

      try {
        /*
         * `no-cors` é exigido por webhooks do Google Apps Script. A resposta é OPACA:
         * este catch só pega falha de rede, nunca um erro devolvido pelo Apps Script.
         * O feedback de sucesso é, portanto, otimista. Não é uma escolha — é a única
         * forma de postar num Apps Script sem CORS a partir do navegador.
         */
        await fetch(this.dataset.webhookUrl, {
          method: 'POST',
          mode: 'no-cors',
          body: payload,
        });

        this.#showSuccess();
        this.#form.reset();
        for (const { key } of FIELDS) {
          this.#inputs[key]?.setAttribute('aria-invalid', 'false');
        }
      } catch (error) {
        console.warn('[capture-form] webhook failed', error);
        this.#setSubmitError(this.dataset.errorSubmit ?? '');
      } finally {
        if (this.#button) {
          this.#button.disabled = false;
          this.#button.removeAttribute('aria-busy');
        }
      }
    };
  }

  if (!customElements.get('capture-form')) {
    customElements.define('capture-form', CaptureForm);
  }
}
